import React, { useCallback, useEffect, useState } from 'react';
import { OnboardingForm } from '@features/onboarding/OnboardingForm';
import { Header } from '@features/header/Header';
import { useHealthStore } from 'src/store/health';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';

import { W2Shell } from './components/W2Shell';
import { W2_TID } from './constants/testids';
import { W2_PHQ_STEP_LABELS } from './copy';
import { W2SelectionStepBar } from './selection/components/W2SelectionStepBar';
import type { StepCell } from './selection/constants/steps';

/**
 * Hosts the reused, branded B2C profile + PHQ (`OnboardingForm`: Welcome →
 * Dependents → the full health questionnaire) inside the tokenized W2 employee
 * flow. W2 mode (see `useW2Mode`) trims the step list to just those three, gates
 * off the B2C `applications` writes, and forks the final submit to
 * `employees.phqPayload` — so this renders the *entire* B2C questionnaire and
 * stops after it (no plans / estimate / payment / contract).
 *
 * Because the W2 flow lives OUTSIDE `PrivateLayout`, we fire here the same
 * read-only reference loads that layout normally does on auth (health issues /
 * conditions drive the PHQ options; plans/providers feed the in-memory
 * transforms). All reads — no B2C side effects.
 */
export const W2Onboarding: React.FC = () => {
  const { getConditions, getIssueDetails, getIssues } = useHealthStore();
  const { getFullPlans, getProviders } = useInsuranceStore();

  const [loadFailed, setLoadFailed] = useState(false);

  // 0-based index into the trimmed W2 step list (Welcome → About You → PHQ).
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const stepCells: StepCell[] = W2_PHQ_STEP_LABELS.map((label, i) => ({
    label,
    state:
      i < currentStep ? 'completed' : i === currentStep ? 'active' : 'future',
  }));

  // Load the PHQ reference data. The loads still run in parallel (happy-path
  // render timing is unchanged — the form renders immediately below while these
  // resolve), but we now AWAIT them so a failed reference load surfaces a
  // retryable error instead of silently stranding the PHQ with empty condition /
  // plan options (finding M1). The store actions swallow their own errors into a
  // per-store `error` field, so we read that as the failure signal after the
  // parallel settle.
  const loadReferenceData = useCallback(async () => {
    setLoadFailed(false);
    await Promise.allSettled([
      getConditions(),
      getIssueDetails(),
      getIssues(),
      getProviders(),
      getFullPlans(),
    ]);
    const healthError = useHealthStore.getState().error;
    const insuranceError = useInsuranceStore.getState().error;
    setLoadFailed(Boolean(healthError || insuranceError));
  }, [getConditions, getIssueDetails, getIssues, getProviders, getFullPlans]);

  useEffect(() => {
    void loadReferenceData();
  }, [loadReferenceData]);

  if (loadFailed) {
    // Reuse the W2 outage/retry shell (same pattern as W2FlowLayout's resolve
    // outage) — a transient reference-data failure gets a retry, not a
    // silently-empty questionnaire.
    return (
      <W2Shell
        showLogout
        title="We couldn’t load your enrollment"
        description="Something went wrong on our side. Please try again in a moment."
      >
        <div data-testid={W2_TID.loadError.root}>
          <button
            type="button"
            data-testid={W2_TID.loadError.retry}
            onClick={() => void loadReferenceData()}
            className="rounded-full bg-primary px-6 py-3 text-[15px] font-semibold text-white hover:opacity-90"
          >
            Try again
          </button>
        </div>
      </W2Shell>
    );
  }

  // Fixed-viewport shell (the shipped `SelectionScaffold` recipe): the frame is
  // exactly one viewport tall with an even 24px inset, <main> is the only
  // scroller, and the inner wrapper is `min-h-full` so it fills when the step is
  // short and GROWS when it is tall (the pinned-`h-full` variant made tall copy
  // unreachable at 1024–1350px).
  return (
    <div className="mx-auto flex h-dvh max-w-[1920px] flex-col gap-3.5 overflow-hidden py-3.5 pb-6 lg:gap-6 lg:pt-6">
      {/* Reuse the branded B2C header so the OneHealth logo renders on the W2
          PHQ screens (logo-only via W2 mode). */}
      <div className="shrink-0">
        <Header showW2Logout />
      </div>
      <main className="min-h-0 flex-1 overflow-y-auto px-6 max-lg:px-4">
        {/* `min-h-full` fills the scrollport and grows with tall content, so
            <main> scrolls and every line stays reachable on small windows. At
            2xl the section is instead PINNED to the scrollport (`h-full`), so a
            tall form can't push the maroon panel's nav buttons off the bottom —
            the content card's own scroller takes the overflow there. */}
        <section className="flex min-h-full flex-col 2xl:h-full">
          <OnboardingForm />
        </section>
      </main>
      {/* Inert progress bar (no `onStepClick`) — back-nav is the Previous
          button. Desktop-only internally; below 2xl the fixed bottom nav bar
          remains the only bottom chrome. */}
      <W2SelectionStepBar steps={stepCells} />
    </div>
  );
};

export default W2Onboarding;
